import React, { useCallback, useEffect, useState } from "react";
import Modal from "../Modal";
import { audiencesService } from "../../services/audiences";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { PLATFORMS } from "../../utils/platforms";
import { formatDate } from "../../utils/date";

/**
 * Manage hand-picked AudienceCreator rows: list, remove, bulk add by handles.
 * Platform comes from the audience; handles must match that platform in our system.
 */
const AudienceRosterModal = ({ audienceHash, audience, isOpen, onClose, onRosterChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [addHandle, setAddHandle] = useState("");

  const platformId = audience?.platform || audience?.category_platform;
  const platformLabel = (platformId && PLATFORMS[platformId]?.name) || platformId || "this audience's platform";

  const loadRoster = useCallback(() => {
    if (!audienceHash || !isOpen) return;
    setLoading(true);
    audiencesService.getAudienceRoster(
      audienceHash,
      (res) => {
        setRows(res.data?.results || []);
        setLoading(false);
        setError(null);
      },
      () => {
        setError("Could not load roster.");
        setLoading(false);
      }
    );
  }, [audienceHash, isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadRoster();
    }
  }, [isOpen, loadRoster]);

  const notifyParent = () => {
    if (onRosterChanged) onRosterChanged();
  };

  const handleRemove = (rosterHash) => {
    audiencesService.deleteAudienceRosterMember(
      audienceHash,
      rosterHash,
      () => {
        loadRoster();
        notifyParent();
      },
      () => setError("Remove failed. Try again.")
    );
  };

  const handleAdd = () => {
    if (!addHandle.trim()) {
      setError("Enter a handle.");
      return;
    }
    setSaving(true);
    setError(null);
    audiencesService.postAudienceRosterBulkAdd(
      audienceHash,
      { handles: addHandle },
      (response) => {
        const body = response?.data || {};
        const added = Number(body.added_count ?? 0);
        const fromApi = Array.isArray(body.creators) ? body.creators : [];
        setAddHandle("");
        setSaving(false);
        if (added === 0) {
          setError(
            "No creators were linked. Check the handle, platform, and spelling. If the profile cannot be loaded from the platform, try again later."
          );
        } else {
          setError(null);
          if (fromApi.length > 0) {
            setRows((prev) => {
              const byHash = new Map(prev.map((r) => [r.hash, r]));
              fromApi.forEach((r) => {
                if (r?.hash) byHash.set(r.hash, r);
              });
              return Array.from(byHash.values()).sort((a, b) => {
                const ta = new Date(a.create_date || 0).getTime();
                const tb = new Date(b.create_date || 0).getTime();
                return tb - ta;
              });
            });
          }
          loadRoster();
        }
        notifyParent();
      },
      (err) => {
        setSaving(false);
        setError(getApiErrorMessage(err, "Could not add creators."));
      }
    );
  };

  const formatFollowers = (n) => {
    if (n == null || Number.isNaN(Number(n))) return "—";
    return Number(n).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <h2 className="text-xl font-semibold text-theme-text mb-2">Hand-picked creators</h2>
      <p className="text-sm text-theme-text opacity-70 mb-4">
        Handles are resolved on {platformLabel}. Only creators that already exist in our system can be linked. New
        handles are ignored until those creators exist.
      </p>
      {error && (
        <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10 text-sm text-theme-text">{error}</div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 mb-4">
        <label htmlFor="audience-roster-add-handle" className="sr-only">
          Add creator handle
        </label>
        <input
          id="audience-roster-add-handle"
          type="text"
          value={addHandle}
          onChange={(e) => setAddHandle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Handles with or without @, comma or space separated"
          className="flex-1 min-w-0 rounded-md border border-theme-secondary bg-theme-main text-theme-text px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || !addHandle.trim()}
          className="shrink-0 px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Adding…" : "Add"}
        </button>
      </div>

      <div className="max-h-80 overflow-auto border border-theme-secondary rounded-md mb-4">
        {loading ? (
          <div className="p-4 text-sm text-theme-text opacity-70">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-theme-text opacity-70">No hand-picked creators yet.</div>
        ) : (
          <table className="w-full text-sm text-theme-text">
            <thead className="bg-theme-main text-left border-b border-theme-secondary">
              <tr>
                <th className="px-3 py-2 font-medium">Handle</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Followers</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Date added</th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const c = row.creator || {};
                const followers = c.follower_count ?? c.subscribers;
                const rowHash = (row.hash || "").trim();
                const rowKey = rowHash || `creator-${c.id ?? c.platform_id ?? "row"}`;
                return (
                  <tr key={rowKey} className="border-b border-theme-secondary">
                    <td className="px-3 py-2 font-mono text-xs">{c.platform_id}</td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatFollowers(followers)}</td>
                    <td className="px-3 py-2 truncate max-w-[140px]">{c.name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-theme-text opacity-80">
                      {formatDate(row.create_date, "—")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          if (!rowHash) {
                            setError(
                              "This row has no roster id yet. Close the dialog and open it again, then try Remove."
                            );
                            return;
                          }
                          handleRemove(rowHash);
                        }}
                        className="text-red-500 hover:opacity-80 text-xs"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-theme-secondary rounded-md text-theme-text hover:bg-theme-secondary hover:opacity-80"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default AudienceRosterModal;
